import { useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

// Constant and Utils
import { formatText } from "../../../common-utils/string-utils/StringUtils";
import { toStringWithRoundUp } from "../../../common-utils/number-utils/NumberUtils";
import { getDifferenceInDays } from "../../../common-utils/date-utils/DateUtils";
import {
  getCitySpecificMediaList,
  getRoadSpecificMediaList,
  getRoadSpecificSelectedMediaCount,
  getSelectedCityMediaCount,
  getSelectedStretchesAndMediaCount,
  getSitesTotalImpressionsAndCost,
} from "./MediaContainerUtil";
import { getReachForDuration } from "../../utils/ReachUtils";

// Components
import TableHeaders from "../../../components/table/TableHeaders";
import MediaContainer from "./MediaContainer";

// table headers
function getTableHeaders(totalMediaCount = 0, selectedMediaCount = 0) {
  return [
    {
      title: {
        displayName: "Name",
        className: "col-3 align-top",
      },
    },
    {
      title: {
        displayName: "Sites Selected",
        className: "col-1 text-center align-top",
      },
      subTitle: {
        displayName: `${selectedMediaCount} / ${totalMediaCount}`,
        className: "d-block text-primary",
      },
    },
    {
      title: {
        displayName: "Est Imp On Stretch",
        className: "col-2 text-left align-top",
      },
    },
    {
      title: {
        displayName: "Total Imp",
        className: "col-2 text-left align-top",
      },
      subTitle: {
        displayName: "(Est Imp * No.of Sites)",
      },
    },
    {
      title: {
        displayName: "Total Reach",
        className: "col-1 text-left align-top",
      },
    },
    {
      title: {
        displayName: "Total Cost (Rs.)",
        className: "col-2 text-center align-top",
      },
    },
    {
      title: {
        displayName: "Status",
        className: "col-1 text-center align-top",
      },
    },
  ];
}

function RoadNameCell({
  roadInfo = {},
  roadMedias = [],
  openMediaContainer,
  setOpenMediaContainer = () => {},
}) {
  const { segmentIds, name } = roadInfo || {};
  const roadType = segmentIds ? "Stretch" : "Segment";
  const roadNameWithType = `${roadType}: ${name}`;

  const isRoadMediasExist = roadMedias.length > 0;
  const iconClassName = openMediaContainer ? "fa-angle-down" : "fa-angle-right";

  return (
    <td className="align-middle col-3">
      <div className="d-flex align-items-center text-truncate max-w-600">
        {isRoadMediasExist && (
          <i
            className={`fa ${iconClassName} mr-1 cursor-pointer`}
            onClick={() => setOpenMediaContainer(!openMediaContainer)}
          />
        )}
        <span title={roadNameWithType} className="text-truncate">
          {roadNameWithType}
        </span>
      </div>
    </td>
  );
}

function SitesSelectedCell({ roadMedias = [], roadSelectedMediaCount = 0 }) {
  const countText = `${roadSelectedMediaCount}/${roadMedias.length}`;

  return (
    <td className="align-middle text-center text-primary col-1">{countText}</td>
  );
}

function TgAndGenImpressions({ estTgSpecific, estGeneric }) {
  const impressionsData = [
    { label: "TG", value: estTgSpecific, className: "mb-1" },
    { label: "Gen", value: estGeneric, className: "mb-1" },
  ];

  return (
    <td className="align-middle col-2">
      <div>
        {impressionsData.map((imp, i) => {
          const { label, value, className } = imp;
          const formattedValue =
            value === 0 ? 0 : formatText(toStringWithRoundUp(value));
          return (
            <p key={i} className={className}>
              {`${label}: ${formattedValue}`}
            </p>
          );
        })}
      </div>
    </td>
  );
}

function TotalCost({ totalCost }) {
  const formattedTotalCost =
    totalCost === 0 ? 0 : formatText(toStringWithRoundUp(totalCost));

  return (
    <td className="align-middle text-center col-2">
      <p>{formattedTotalCost}</p>
    </td>
  );
}

function SelectionStatus({ roadStretchId }) {
  const selectedMediaCount = useSelector(
    (state) => state.mediaSelection.mediaStretchSelectedMap[roadStretchId]
  );

  const borderClassName = selectedMediaCount ? "border-success" : "";
  const iconName = selectedMediaCount
    ? "fa-check text-success"
    : "fa-minus text-secondary";

  return (
    <td className="text-center align-middle col-1">
      <span
        className={`border p-2 rounded-lg cursor-pointer ${borderClassName}`}
      >
        <i className={`fa ${iconName}`}></i>
      </span>
    </td>
  );
}

function AvgReachCell({ roadInfo = {}, cityPlan = {} }) {
  const { startTimestamp, endTimestamp } = cityPlan || {};
  const { reachAvg } = roadInfo || {};

  // city plan duration
  const cityPlanDuration =
    getDifferenceInDays(startTimestamp, endTimestamp) - 1;

  // reach for duration
  const durationReach = getReachForDuration(reachAvg, cityPlanDuration);

  const formattedAvgReach =
    durationReach === 0 ? 0 : formatText(toStringWithRoundUp(durationReach));

  return (
    <td className="align-middle col-1">
      <p>{formattedAvgReach}</p>
    </td>
  );
}

// Road Details Row
function RoadDetailsRow({
  roadInfo = {},
  cityPlan = {},
  campaignMediasMap = {},
}) {
  const { cityId } = useParams();

  // State
  const [openMediaContainer, setOpenMediaContainer] = useState(true);

  // campaignMedias : selectedMediaBooleanMap : { mediaId: true, mediaId: false }
  const selectedMediaBooleanMap = useSelector(
    (state) => state.mediaSelection.selectedMedia
  );

  // if roadInfo not present
  if (!roadInfo || Object.keys(roadInfo).length === 0) {
    return null;
  }

  const { roadStretchOtsMap = {} } = cityPlan || {};
  const { id: roadId, orgMedias: roadMedias = [] } = roadInfo || {};

  // road specific selected media count
  const roadSelectedMediaCount = getRoadSpecificSelectedMediaCount(
    roadMedias,
    selectedMediaBooleanMap
  );

  // ots values of current stretch
  const stretchFromCityPlan = roadStretchOtsMap[roadId];
  const { ots, otsLit, targetOts, targetOtsLit } = stretchFromCityPlan || {};
  const finalOts = otsLit ?? ots;
  const finalTargetOts = targetOtsLit ?? targetOts;

  // city specific medias
  const campaignMedias = Object.values(campaignMediasMap);
  const citySpecificMediaList = getCitySpecificMediaList(
    campaignMedias,
    cityId
  );

  // road specific media
  const roadSpecificMediaList = getRoadSpecificMediaList(
    roadId,
    citySpecificMediaList
  );

  // total cost
  const { totalEstCost } = getSitesTotalImpressionsAndCost(
    roadSpecificMediaList
  );

  return (
    <>
      <tr>
        <RoadNameCell
          roadInfo={roadInfo}
          roadMedias={roadMedias}
          openMediaContainer={openMediaContainer}
          setOpenMediaContainer={setOpenMediaContainer}
        />
        <SitesSelectedCell
          roadMedias={roadMedias}
          roadSelectedMediaCount={roadSelectedMediaCount}
        />
        <TgAndGenImpressions
          estTgSpecific={finalTargetOts}
          estGeneric={finalOts}
        />
        <TgAndGenImpressions
          estTgSpecific={finalTargetOts * roadSelectedMediaCount}
          estGeneric={finalOts * roadSelectedMediaCount}
        />
        <AvgReachCell roadInfo={roadInfo} cityPlan={cityPlan} />
        <TotalCost totalCost={totalEstCost} />
        <SelectionStatus roadStretchId={roadId} />
      </tr>

      {openMediaContainer && (
        <tr>
          <td className="p-0" colSpan={7}>
            <MediaContainer
              roadInfo={roadInfo}
              cityPlan={cityPlan}
              roadMedias={roadMedias}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function AddMediaSection() {
  // const dispatch = useDispatch();

  // create Media Loading
  // const createMediaLoading = useSelector(
  //   (state) => state.mediaSelection.createMediaLoading
  // );

  return (
    <div className="d-flex justify-content-between">
      <h4>Site Details</h4>

      {/* <div className="d-flex">
        {createMediaLoading && <Spinner className="spinner-border-sm mr-2" />}
        <span
          className="text-primary cursor-pointer"
          data-toggle="modal"
          data-target={`#${ProohFormDataTargets.sellerCreateMediaForm}`}
          onClick={() => dispatch(openCreateMediaFormModal())}
        >
          {"Add a site"}
        </span>
      </div> */}
    </div>
  );
}

// getting all stretches by grouping them according to zones
function getAllGroupedStretches(selectedStretchesMap = {}) {
  const zoneIds = Object.values(selectedStretchesMap).map(
    (stretch) => stretch.zoneId
  );

  const uniqueZoneIds = [...new Set(zoneIds)];
  const zoneGroupedStretchesIds = [];
  const nonZoneGroupedStretchesIds = [];

  uniqueZoneIds.forEach((uniqueZoneId) => {
    Object.values(selectedStretchesMap).forEach((stretchInfo) => {
      const { zoneId, id } = stretchInfo;

      // Pushing non zone associated stretches to nonZoneGrouped
      if (
        !zoneId &&
        !nonZoneGroupedStretchesIds.includes(id) &&
        !uniqueZoneId
      ) {
        nonZoneGroupedStretchesIds.push(id);
        return;
      }

      // Pushing zone associated stretches to zoneGrouped
      if (uniqueZoneId === zoneId) {
        zoneGroupedStretchesIds.push(id);
      }
    });
  });

  // combining both by keeping non zone at last
  const allGroupedStretches = [
    ...zoneGroupedStretchesIds,
    ...nonZoneGroupedStretchesIds,
  ];
  return allGroupedStretches;
}

/**
 * Page Section
 */
export default function RoadAndMediaSection({
  cityPlan = {},
  campaignMediasMap = {}, // campaign medias map
}) {
  // road stretches data
  const roadStretchDetailsMap = useSelector(
    (state) => state.planningRoadStretches.roadStretchDetailsMap
  );

  // selected city specific campaign medias
  const selectedCityMediaCount = getSelectedCityMediaCount(
    cityPlan,
    campaignMediasMap
  );

  // selected stretch data and total mediaCount on stretch
  const { selectedStretchesMap, totalMediaCount } =
    getSelectedStretchesAndMediaCount(cityPlan, roadStretchDetailsMap);

  if (Object.keys(cityPlan).length < 1) {
    return null;
  }

  const tableHeaders = getTableHeaders(totalMediaCount, selectedCityMediaCount);

  // getting all stretches by grouping them according to zones
  const allGroupedStretches = getAllGroupedStretches(selectedStretchesMap);

  return (
    <div className="mt-5">
      <AddMediaSection />

      {/* Table */}
      <div className="table-responsive">
        <table className="table">
          <TableHeaders tableHeaders={tableHeaders} headerClass={"thead"} />
          <tbody>
            {allGroupedStretches.map((eachStretchId) => {
              const roadInfo = selectedStretchesMap[eachStretchId];
              return (
                <RoadDetailsRow
                  key={eachStretchId}
                  roadInfo={roadInfo}
                  cityPlan={cityPlan}
                  campaignMediasMap={campaignMediasMap}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
